// src/lib/arc.ts
// ─────────────────────────────────────────────────────────────────────────────
// App-local "Your data" section composition for the user home.
//
// The ARC tile band itself is now server-composed and rendered by the SDK's
// <PulseRecommendations/> (GET /api/pulse/arc). What remains here is purely the
// section-card grid: ranking the app's own data domains by a simple activity
// proxy so the most-used domain surfaces first.
// ─────────────────────────────────────────────────────────────────────────────

export type SectionHref = "/body" | "/training" | "/peptides" | "/conversations";

export interface SectionCard {
  href: SectionHref;
  label: string;
  summary: string;
  highlighted: boolean;
}

export interface ComposeSectionsInput {
  bodyCount: number;
  programCount: number;
  goalCount: number;
  peptidePlanCount: number;
  peptideDoseCount: number;
}

export function composeSections(input: ComposeSectionsInput): SectionCard[] {
  const ranked: Array<{ card: SectionCard; weight: number }> = [
    {
      card: {
        href: "/body",
        label: "My Body",
        summary: input.bodyCount
          ? `${input.bodyCount} metric${input.bodyCount === 1 ? "" : "s"} logged`
          : "No body metrics yet",
        highlighted: false,
      },
      weight: input.bodyCount,
    },
    {
      card: {
        href: "/training",
        label: "My Training",
        summary: `${input.programCount} program${input.programCount === 1 ? "" : "s"} · ${input.goalCount} goal${input.goalCount === 1 ? "" : "s"}`,
        highlighted: false,
      },
      weight: input.programCount + input.goalCount,
    },
    {
      card: {
        href: "/peptides",
        label: "My Peptides",
        summary: `${input.peptidePlanCount} plan${input.peptidePlanCount === 1 ? "" : "s"} · ${input.peptideDoseCount} dose${input.peptideDoseCount === 1 ? "" : "s"}`,
        highlighted: false,
      },
      weight: input.peptidePlanCount + input.peptideDoseCount,
    },
    {
      card: {
        href: "/conversations",
        label: "My Conversations",
        summary: "Specialist chat thread",
        highlighted: false,
      },
      weight: 0,
    },
  ];

  // Highlight the single most-active domain (if any data exists) and rank the
  // rest by signal weight descending.
  ranked.sort((a, b) => b.weight - a.weight);
  if (ranked[0] && ranked[0].weight > 0) ranked[0].card.highlighted = true;

  return ranked.map(({ card }) => card);
}
